import React, { useState, useContext } from 'react'
import styled, { css } from 'styled-components'
import { bool } from 'prop-types'
import { stripUnit, rgba } from 'polished'
import ScrollAnimation from 'react-animate-on-scroll'
import { Link } from 'gatsby'
import { CopyToClipboard } from 'react-copy-to-clipboard'

import { white, green, black } from 'styles/colors'
import * as spacing from 'styles/spacing'
import { media } from 'styles/media'
import { borderRadius, animationTime, animationCurve } from 'styles/global'
import { H2, fontWeights, fontSizes, Lead, lineHeights } from 'styles/typography'
import SaasquatchContext from 'contexts/Saasquatch'

import Wrapper from 'components/Wrapper'
import Section, { SectionBlock } from 'components/Section'
import Icon from 'components/Icon'
import Button from 'components/button/Button'
import { NativeAuthConsumer } from 'components/NativeAuth'
import Mail from 'icons/mail.svg'
import Checkmark from 'icons/checkmark.svg'
import Duplicate from 'icons/duplicate.svg'

const Container = styled.div`
  position: static;

  ${media.small`
    margin-top: -${spacing.large};
  `};

  ${media.medium`
    margin-top: -${spacing.xLarge};
  `};

  ${media.large`
    margin-top: -${spacing.xxLarge};
  `};
`

const Header = styled.header`
  text-align: center;
  margin-left: auto;
  margin-right: auto;
  max-width: 460px;

  ${media.medium`
    max-width: 600px;
  `};

  ${media.large`
    max-width: 700px;
  `};

  ${media.xLarge`
    max-width: 800px;
  `};
`

const Heading = styled(H2)`
  color: ${white};
  line-height: ${lineHeights.base};
  text-transform: uppercase;
  font-weight: ${fontWeights.regular};
  letter-spacing: 0.03em;
  margin-bottom: 0.5em;

  ${media.xLarge`
    margin-bottom: 0.75em;
  `};
`

const Subheading = styled(Lead)`
  color: ${white};
`

const Field = styled.div`
  position: static;
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;
  margin-bottom: ${spacing.large};

  ${media.small`
    margin-bottom: ${spacing.xLarge};
  `};

  ${media.medium`
    margin-bottom: ${spacing.xxLarge};
  `};

  ${media.large`
    margin-bottom: ${spacing.xxxLarge};
  `};

  ${media.xLarge`
    margin-bottom: 60px;
  `};

  &:last-child {
    margin-bottom: 0;
  }
`

const Label = styled.span`
  display: block;
  font-size: ${fontSizes.small};
  margin-bottom: ${spacing.small};
  color: ${white};
  font-weight: ${fontWeights.medium};

  ${media.small`
    font-size: 16px;
  `};

  ${media.medium`
    font-size: 17px;
  `};

  ${media.large`
    font-size: 18px;
  `};

  ${media.xLarge`
    font-size: 20px;
    margin-bottom: ${spacing.medium};
  `};
`

const InputWrap = styled.div`
  display: block;

  ${media.small`
    display: flex;
    align-items: center;
    justify-content: space-between;
  `};
`

const Input = styled.input`
  background-color: ${white};
  color: ${black};
  width: 100%;
  display: block;
  padding: 0 ${stripUnit(spacing.medium) * 1.25 + 'px'};
  height: 56px;
  border-radius: ${borderRadius};

  ${media.small`
    flex: 1;
  `};
`

const Action = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: ${white};
  text-transform: uppercase;
  padding: 0 ${stripUnit(spacing.medium) * 1.25 + 'px'};
  height: 56px;
  border: 1px solid ${white};
  border-radius: ${borderRadius};
  margin-top: ${stripUnit(spacing.small) * 1.5 + 'px'};
  width: 100%;
  flex-shrink: 0;
  transition: background-color ${animationTime} ${animationCurve}, border-color ${animationTime} ${animationCurve};

  ${props =>
    props.copied &&
    css`
      background-color: ${green};
      border-color: ${green};
    `};

  ${media.small`
    width: 220px;
    margin-left: ${spacing.medium};
    margin-top: 0;
  `};

  ${media.medium`
    width: 260px;
  `};

  ${media.large`
    margin-left: ${stripUnit(spacing.medium) * 1.25 + 'px'};
  `};
`

const ButtonWrap = styled.div`
  text-align: center;

  ${media.medium`
    margin-top: ${spacing.large};
  `};

  ${media.large`
    margin-top: ${spacing.xLarge};
  `};

  ${media.xLarge`
    margin-top: ${spacing.xxLarge};
  `};
`

const ActionIcon = styled.div`
  display: flex;
  margin-right: ${spacing.medium};

  ${media.small`
    margin-right: ${stripUnit(spacing.small) * 1.5 + 'px'};
  `};

  ${media.medium`
    margin-right: ${spacing.medium};
  `};

  ${media.xLarge`
    margin-right: ${spacing.large};
  `};
`

const Disclaimer = styled.p`
  text-align: center;
  font-size: ${fontSizes.small};
  color: ${rgba(white, 0.75)};

  a {
    color: inherit;
  }
`

const MobileToast = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${white};
  background-color: ${green};
  z-index: 400;
  text-transform: uppercase;
  padding: ${stripUnit(spacing.small) * 1.5 + 'px'};
`

const MobileToastIcon = styled.div`
  display: flex;
  margin-right: ${stripUnit(spacing.small) * 1.5 + 'px'};
`

const Share = () => {
  const { shareUrl, eligible } = useContext(SaasquatchContext)
  const [copied, setCopied] = useState(false)

  return (
    <NativeAuthConsumer>
      {({ user }) => {
        return user && eligible ? (
          <>
            <Container id="savings">
              <Section>
                <Wrapper>
                  <SectionBlock>
                    <Header as={ScrollAnimation} animateIn="fadeInUp" offset={0} delay={200} animateOnce>
                      <Heading>Start passing on the savings</Heading>
                      <Subheading>Copy and share your personalised invite link or send them in an email.</Subheading>
                    </Header>
                  </SectionBlock>
                  {eligible && (
                    <SectionBlock>
                      <ScrollAnimation animateIn="fadeInUp" offset={0} delay={400} animateOnce>
                        <Field>
                          <Label>Your invite link</Label>

                          <InputWrap>
                            <Input value={shareUrl} readOnly />
                            <CopyToClipboard
                              text={shareUrl}
                              onCopy={() => {
                                setCopied(true)

                                setTimeout(() => {
                                  setCopied(false)
                                }, 3000)
                              }}
                            >
                              <Action copied={copied}>
                                <ActionIcon>
                                  <Icon icon={copied ? <Checkmark /> : <Duplicate />} />
                                </ActionIcon>
                                {copied ? 'Link copied' : 'Copy'}
                              </Action>
                            </CopyToClipboard>
                          </InputWrap>
                        </Field>
                        <Field>
                          <InputWrap>
                            <Label
                              css={`
                                margin-bottom: 0 !important;
                              `}
                            >
                              Share the savings by email
                            </Label>
                            <Action
                              as="a"
                              href={`mailto:?subject=Time to Pass on the Savings!&body=Time to Pass on the Savings!%0d%0a%0d%0aUse this link: ${shareUrl} to sign up to a Vuse account and get a $20 credit on vuse.com, which can be used on any order over $45. After you make your first purchase, it will be my turn to get savings!%0d%0a%0d%0aParticipation in this program is subject to the Pass on the Savings Terms and Conditions: https://www.vuse.com/ca/en/pages/savings-terms-and-conditions/`}
                            >
                              <ActionIcon>
                                <Icon icon={<Mail />} />
                              </ActionIcon>
                              Send
                            </Action>
                          </InputWrap>
                        </Field>
                      </ScrollAnimation>
                    </SectionBlock>
                  )}
                  <ScrollAnimation animateIn="fadeInUp" offset={0} delay={600} animateOnce>
                    <SectionBlock>
                      <Disclaimer>
                        Participation in this program is subject to the Pass on the Savings{' '}
                        <Link to="/pages/savings-terms-and-conditions/">Terms and Conditions</Link>
                      </Disclaimer>
                    </SectionBlock>
                  </ScrollAnimation>
                  <SectionBlock>
                    <ButtonWrap as={ScrollAnimation} animateIn="fadeInUp" offset={0} delay={800} animateOnce>
                      <Button to="/account/savings/" theme="custom" customColor="#d2be8a" size="large">
                        Track your savings
                      </Button>
                    </ButtonWrap>
                  </SectionBlock>
                </Wrapper>
              </Section>
            </Container>

            {copied && (
              <MobileToast className="u-hide-small-up">
                <MobileToastIcon>
                  <Icon icon={<Checkmark />} />
                </MobileToastIcon>
                Link copied
              </MobileToast>
            )}
          </>
        ) : null
      }}
    </NativeAuthConsumer>
  )
}

Share.propTypes = {
  hidden: bool
}

export default Share
